import { FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { Box, CircularProgress, Tooltip, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

import { StyledButton } from "../../common/styled/button";
import { IMediaSupport, mediaSupport } from "./media-support";

const StyledImage = styled("img")<{ clickable?: boolean }>(({ clickable }) => ({
  cursor: clickable ? "pointer" : "default",
  maxWidth: "100%",
  maxHeight: "50vh",
  objectFit: "contain",
  marginBottom: "10px",
}));

export interface IMediaFile {
  url?: string;
  contentType: string;
  fileName?: string;
  isPreviewLoading?: boolean;
  isDownloading?: boolean;
}

interface IMediaRendererProps {
  file: IMediaFile;
  index: number;
  onImageClick?: (url: string) => void;
  onDownload: (file: IMediaFile) => void;
  mediaSupport?: IMediaSupport;
  videoSupport?: IMediaSupport;
}

export const MediaRenderer: FC<IMediaRendererProps> = ({
  file,
  index,
  onImageClick,
  onDownload,
  mediaSupport: mediaSupportProp,
  videoSupport: videoSupportProp,
}) => {
  const { formatMessage } = useIntl();

  const { url, contentType: type } = file;
  const support = mediaSupportProp ?? videoSupportProp ?? mediaSupport;

  const renderMedia = () => {
    if (type.startsWith("image/")) {
      if (!url) {
        return null;
      }

      const image = (
        <StyledImage
          src={url}
          alt={file.fileName ?? `file-${index}`}
          clickable={!!onImageClick}
          onClick={() => onImageClick?.(url)}
        />
      );

      if (!onImageClick) {
        return image;
      }

      return <Tooltip title={formatMessage({ id: "form.hints.openFullSizeImage" })}>{image}</Tooltip>;
    }

    if (type.startsWith("audio/")) {
      if (!url) {
        return null;
      }

      return <Box component="audio" src={url} controls width="100%" />;
    }

    if (type.startsWith("video/")) {
      if (!type.endsWith("/*") && support.canPlay(type)) {
        if (!url) {
          return null;
        }

        return <Box component="video" src={url} controls sx={{ maxWidth: "100%", maxHeight: "50vh" }} />;
      }

      return (
        <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: "bold", mb: 2 }}>
          <FormattedMessage id="form.hints.videoFormatNotSupported" />
        </Typography>
      );
    }

    return null;
  };

  return (
    <Box display="flex" flexDirection="column" gap={1} sx={{ minWidth: 300 }}>
      {file.isPreviewLoading ? <CircularProgress size={24} /> : renderMedia()}

      <StyledButton
        disabled={file.isPreviewLoading || file.isDownloading}
        onClick={() => onDownload(file)}
        sx={{ minWidth: 120, alignSelf: "flex-start" }}
      >
        {file.isDownloading && <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} />}
        <FormattedMessage id="dialogs.download" />
      </StyledButton>
    </Box>
  );
};
